import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '../../app/store'
import { AuthState, User } from '../../types'
import api from '../../services/api'
import { getAuthToken, setAuthToken, removeAuthToken, getUserData, setUserData, removeUserData, checkIsAuthenticated } from '../../utils/secureStorage'

// Initial state - matches Flutter's initial state exactly
const initialState: AuthState = {
  user: getUserData(),
  token: getAuthToken(),
  isLoading: false,
  error: null,
  isAuthenticated: checkIsAuthenticated(),
  hasActiveShift: localStorage.getItem('has_active_shift') === 'true',
  lastShiftCheck: localStorage.getItem('last_shift_check'),
}

const clearUserLocalStorage = () => {
  const keysToClear = [
    'auth_token',
    'user_data',
    'employee_data',
    'has_active_shift',
    'last_shift_check',
    'temp_employee_id',
    'selected_branch_data',
    'selected_address_id',
    'current_shift',
    'cs_cart_id',
    'persist:root',
  ]

  keysToClear.forEach((key) => {
    localStorage.removeItem(key)
  })
}

// Login API call - matches Flutter's login logic exactly
export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async (credentials: { username: string; password: string }, { rejectWithValue }) => {
    try {
      const response = await api.post('/cashier/auth/login', credentials)

      if (response.status === 200) {
        const data = response.data
        
        // Check success conditions - matches Flutter logic exactly
        if (data.success !== true && data.status !== 'success') {
          return rejectWithValue(data.message || 'Login failed, check credentials')
        }

        return data
      } else {
        return rejectWithValue('Login failed, check credentials')
      }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Enter the correct number')
    }
  }
)

// Login with PIN - matches Flutter's PIN login logic exactly
export const loginPin = createAsyncThunk(
  'auth/loginPin',
  async ({ username, password }: { username: string; password: string }, { rejectWithValue }) => {
    try {
      const response = await api.post('/cashier/auth/login', {
        username: username,
        password: password,
      })

      if (response.status === 200) {
        const data = response.data
        // If response is successful (status 200), accept it
        // Don't check for specific success fields - just trust the 200 status
        return data
      } else {
        return rejectWithValue('Login failed, check credentials')
      }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Enter the correct number')
    }
  }
)

// Check active shift - matches Flutter's shift check logic exactly
export const checkActiveShift = createAsyncThunk(
  'auth/checkActiveShift',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState
      const token = state.auth.token

      if (!token) {
        return rejectWithValue('No authentication token')
      }

      const response = await api.get('/cashier/shifts')

      if (response.status === 200) {
        const data = response.data
        
        // Check for active shift - matches Flutter logic exactly
        const shifts = data.data?.shifts || data.shifts || []
        const activeShift = shifts.find((shift: any) => 
          shift.status === 'active' || shift.status === 'open'
        )

        return !!activeShift
      } else {
        return rejectWithValue('Failed to check shift status')
      }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to check shift status')
    }
  }
)

// Open shift - matches Flutter's shift opening logic exactly
export const openShift = createAsyncThunk(
  'auth/openShift',
  async (openingBalance: string, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState
      const token = state.auth.token

      if (!token) {
        return rejectWithValue('No authentication token')
      }

      // Validate amount - matches Flutter validation exactly
      const amount = parseFloat(openingBalance)
      if (isNaN(amount) || amount < 0) {
        return rejectWithValue('يرجى إدخال مبلغ صحيح')
      }

      // Check decimal places - matches Flutter logic exactly
      if (openingBalance.includes('.')) {
        const parts = openingBalance.split('.')
        if (parts.length > 1 && parts[1].length > 2) {
          return rejectWithValue('المبلغ لا يمكن أن يحتوي على أكثر من رقمين عشريين')
        }
      }

      const response = await api.post('/cashier/shifts', { 
        opening_balance: openingBalance 
      })

      if (response.status === 200 || response.status === 201) {
        const data = response.data
        
        // Check success conditions - matches Flutter logic exactly
        const success = data.success === true || 
                       data.success === 'true' || 
                       data.success === 'success' ||
                       data.status === 'success' ||
                       data.status === 'true' ||
                       data.status === true ||
                       data.message === 'success' ||
                       data.message === 'Success' ||
                       (response.status === 200 && data.data != null) ||
                       (response.status === 201 && data.data != null)

        if (!success) {
          return rejectWithValue(data.message || 'فشل فتح الوردية')
        }

        return data
      } else {
        return rejectWithValue('حدث خطأ بالخادم')
      }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'تعذر الاتصال بالخادم')
    }
  }
)

// Auth slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload
    },
    clearError: (state) => {
      state.error = null
    },
    logout: (state) => {
      state.user = null
      state.token = null
      state.isAuthenticated = false
      state.hasActiveShift = false
      state.lastShiftCheck = null
      state.error = null
      clearUserLocalStorage()
      
      // Clear token from Axios instance
      delete api.defaults.headers.Authorization
    },
    clearTempData: () => {
      localStorage.removeItem('temp_employee_id')
    },
    setTempEmployeeId: (_, action: PayloadAction<string>) => {
      localStorage.setItem('temp_employee_id', action.payload)
    },
    // Legacy actions for compatibility
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload
      state.isAuthenticated = true
      localStorage.setItem('user_data', JSON.stringify(action.payload))
    },
    setToken: (state, action: PayloadAction<string>) => {
      state.token = action.payload
      state.isAuthenticated = true
      setAuthToken(action.payload)
      
      // Set token in Axios instance
      api.defaults.headers.Authorization = `Bearer ${action.payload}`
    },
    clearAuth: (state) => {
      state.user = null
      state.token = null
      state.isAuthenticated = false
      state.hasActiveShift = false
      state.lastShiftCheck = null
      state.error = null
      clearUserLocalStorage()
      
      // Clear token from Axios instance
      delete api.defaults.headers.Authorization
    },
    updateUser: (state, action: PayloadAction<Partial<User>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload }
        localStorage.setItem('user_data', JSON.stringify(state.user))
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Login cases
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false
        state.error = null
        
        const { data } = action.payload
        
        // Set token - matches Flutter logic exactly
        if (data.token) {
          state.token = data.token
          state.isAuthenticated = true
          setAuthToken(data.token)
          
          // Set token in Axios instance for future requests
          api.defaults.headers.Authorization = `Bearer ${data.token}`
        }
        
        // Set user data - matches Flutter logic exactly
        if (data.employee) {
          state.user = data.employee
          localStorage.setItem('user_data', JSON.stringify(data.employee))
          // Also save as employee_data for CS flow compatibility
          // Include branches and orderTypes from data if available
          localStorage.setItem('employee_data', JSON.stringify({
            ...data.employee,
            user_type: data.employee.type || data.employee.user_type,
            type: data.employee.type || data.employee.user_type,
            orderTypes: data.employee.orderTypes || data.orderTypes || [],
            branches: data.employee.branches || data.branches || [],
          }))
        }
        
        // Set shift status - matches Flutter logic exactly
        const hasActiveShift = data.employee?.has_active_shift || false
        state.hasActiveShift = hasActiveShift
        localStorage.setItem('has_active_shift', hasActiveShift.toString())
        
        const now = new Date().toISOString()
        state.lastShiftCheck = now
        localStorage.setItem('last_shift_check', now)
        
        // Clear temp data - matches Flutter logic exactly
        localStorage.removeItem('temp_employee_id')
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      
      // Login PIN cases
      .addCase(loginPin.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(loginPin.fulfilled, (state, action) => {
        state.isLoading = false
        state.error = null
        
        // Handle different payload structures - check both data.data and data
        const payload = action.payload
        let data = (typeof payload === 'object' && payload !== null) ? payload : {}
        
        // Check for nested data structure (data.data)
        if (data.data && typeof data.data === 'object') {
          data = { ...data, ...data.data }
        }
        
        // Set token - check multiple possible locations
        const token = data.token || data.access_token || data.auth_token
        if (token) {
          state.token = token
          state.isAuthenticated = true
          setAuthToken(token)
          
          // Set token in Axios instance for future requests
          api.defaults.headers.Authorization = `Bearer ${token}`
        } else {
        }
        
        // Set user data - check multiple possible locations
        const user = data.employee || data.user || data.data?.employee || data.data?.user
        if (user) {
          state.user = user
          state.isAuthenticated = true // Ensure authenticated if user exists
          localStorage.setItem('user_data', JSON.stringify(user))
          // Also save as employee_data for CS flow compatibility
          // Include branches and orderTypes from data if available
          const employeeData = {
            ...user,
            user_type: user.type || user.user_type,
            type: user.type || user.user_type,
            orderTypes: user.orderTypes || data.orderTypes || data.data?.orderTypes || [],
            branches: user.branches || data.branches || data.data?.branches || [],
          }
          localStorage.setItem('employee_data', JSON.stringify(employeeData))
        } else {
        }
        
        // Ensure isAuthenticated is true if we have either token or user
        if (token || user) {
          state.isAuthenticated = true
        }
        
        // Set shift status - matches Flutter logic exactly
        const hasActiveShift = user?.has_active_shift || data.has_active_shift || false
        state.hasActiveShift = hasActiveShift
        localStorage.setItem('has_active_shift', hasActiveShift.toString())
        
        const now = new Date().toISOString()
        state.lastShiftCheck = now
        localStorage.setItem('last_shift_check', now)
        
        // Clear temp data - matches Flutter logic exactly
        localStorage.removeItem('temp_employee_id')
      })
      .addCase(loginPin.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      
      // Check active shift cases
      .addCase(checkActiveShift.fulfilled, (state, action) => {
        state.hasActiveShift = action.payload
        localStorage.setItem('has_active_shift', action.payload.toString())
        
        const now = new Date().toISOString()
        state.lastShiftCheck = now
        localStorage.setItem('last_shift_check', now)
      })
      
      // Open shift cases
      .addCase(openShift.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(openShift.fulfilled, (state) => {
        state.isLoading = false
        state.error = null
        state.hasActiveShift = true
        localStorage.setItem('has_active_shift', 'true')
        
        const now = new Date().toISOString()
        state.lastShiftCheck = now
        localStorage.setItem('last_shift_check', now)
      })
      .addCase(openShift.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
  },
})

export const {
  setLoading, 
  setError, 
  clearError,
  logout, 
  clearTempData, 
  setTempEmployeeId,
  setUser,
  setToken,
  clearAuth,
  updateUser
} = authSlice.actions

export default authSlice.reducer